import React, { useState, useEffect, useCallback } from 'react';
import { useAudioCapture } from '@/hooks/useAudioCapture';
import { useMetrics } from '@/hooks/useMetrics';
import { adminClient } from '@/api/adminClient';
import AudioVisualizer from '@/components/AudioVisualizer';
import PerformanceDashboard from '@/components/PerformanceDashboard';
import type { AudioInputSettings, Room } from '@/types';

// All supported source languages for Azure Speech Recognition
// Primary languages shown as buttons, rest available in dropdown
const PRIMARY_SOURCE_LANGUAGES = [
  { code: 'ko', label: '한국어', flag: '🇰🇷' },
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'zh', label: '中文', flag: '🇨🇳' },
  { code: 'ja', label: '日本語', flag: '🇯🇵' },
];

// Extended language list - 50+ languages supported by Azure Speech SDK
const ALL_SOURCE_LANGUAGES = [
  // Primary (shown as buttons)
  { code: 'ko', label: '한국어', flag: '🇰🇷', region: 'Asia' },
  { code: 'en', label: 'English', flag: '🇺🇸', region: 'Americas' },
  { code: 'zh', label: '中文 (简体)', flag: '🇨🇳', region: 'Asia' },
  { code: 'ja', label: '日本語', flag: '🇯🇵', region: 'Asia' },
  // East Asia
  { code: 'zh-Hant', label: '中文 (繁體)', flag: '🇹🇼', region: 'Asia' },
  { code: 'vi', label: 'Tiếng Việt', flag: '🇻🇳', region: 'Asia' },
  { code: 'th', label: 'ไทย', flag: '🇹🇭', region: 'Asia' },
  { code: 'id', label: 'Bahasa Indonesia', flag: '🇮🇩', region: 'Asia' },
  { code: 'ms', label: 'Bahasa Melayu', flag: '🇲🇾', region: 'Asia' },
  { code: 'fil', label: 'Filipino', flag: '🇵🇭', region: 'Asia' },
  { code: 'my', label: 'မြန်မာ', flag: '🇲🇲', region: 'Asia' },
  { code: 'km', label: 'ខ្មែរ', flag: '🇰🇭', region: 'Asia' },
  { code: 'lo', label: 'ລາວ', flag: '🇱🇦', region: 'Asia' },
  // South Asia
  { code: 'hi', label: 'हिन्दी', flag: '🇮🇳', region: 'Asia' },
  { code: 'bn', label: 'বাংলা', flag: '🇧🇩', region: 'Asia' },
  { code: 'ta', label: 'தமிழ்', flag: '🇮🇳', region: 'Asia' },
  { code: 'te', label: 'తెలుగు', flag: '🇮🇳', region: 'Asia' },
  { code: 'mr', label: 'मराठी', flag: '🇮🇳', region: 'Asia' },
  { code: 'gu', label: 'ગુજરાતી', flag: '🇮🇳', region: 'Asia' },
  { code: 'kn', label: 'ಕನ್ನಡ', flag: '🇮🇳', region: 'Asia' },
  { code: 'ml', label: 'മലയാളം', flag: '🇮🇳', region: 'Asia' },
  { code: 'ne', label: 'नेपाली', flag: '🇳🇵', region: 'Asia' },
  { code: 'si', label: 'සිංහල', flag: '🇱🇰', region: 'Asia' },
  { code: 'ur', label: 'اردو', flag: '🇵🇰', region: 'Asia' },
  // Middle East
  { code: 'ar', label: 'العربية', flag: '🇸🇦', region: 'Middle East' },
  { code: 'fa', label: 'فارسی', flag: '🇮🇷', region: 'Middle East' },
  { code: 'he', label: 'עברית', flag: '🇮🇱', region: 'Middle East' },
  { code: 'tr', label: 'Türkçe', flag: '🇹🇷', region: 'Middle East' },
  // Europe - Western
  { code: 'de', label: 'Deutsch', flag: '🇩🇪', region: 'Europe' },
  { code: 'fr', label: 'Français', flag: '🇫🇷', region: 'Europe' },
  { code: 'es', label: 'Español', flag: '🇪🇸', region: 'Europe' },
  { code: 'pt', label: 'Português', flag: '🇵🇹', region: 'Europe' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹', region: 'Europe' },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱', region: 'Europe' },
  { code: 'ca', label: 'Català', flag: '🇪🇸', region: 'Europe' },
  // Europe - Northern
  { code: 'sv', label: 'Svenska', flag: '🇸🇪', region: 'Europe' },
  { code: 'da', label: 'Dansk', flag: '🇩🇰', region: 'Europe' },
  { code: 'no', label: 'Norsk', flag: '🇳🇴', region: 'Europe' },
  { code: 'fi', label: 'Suomi', flag: '🇫🇮', region: 'Europe' },
  // Europe - Eastern
  { code: 'ru', label: 'Русский', flag: '🇷🇺', region: 'Europe' },
  { code: 'pl', label: 'Polski', flag: '🇵🇱', region: 'Europe' },
  { code: 'uk', label: 'Українська', flag: '🇺🇦', region: 'Europe' },
  { code: 'cs', label: 'Čeština', flag: '🇨🇿', region: 'Europe' },
  { code: 'sk', label: 'Slovenčina', flag: '🇸🇰', region: 'Europe' },
  { code: 'hu', label: 'Magyar', flag: '🇭🇺', region: 'Europe' },
  { code: 'ro', label: 'Română', flag: '🇷🇴', region: 'Europe' },
  { code: 'bg', label: 'Български', flag: '🇧🇬', region: 'Europe' },
  { code: 'hr', label: 'Hrvatski', flag: '🇭🇷', region: 'Europe' },
  { code: 'sr', label: 'Српски', flag: '🇷🇸', region: 'Europe' },
  { code: 'sl', label: 'Slovenščina', flag: '🇸🇮', region: 'Europe' },
  { code: 'el', label: 'Ελληνικά', flag: '🇬🇷', region: 'Europe' },
  // Europe - Baltic
  { code: 'et', label: 'Eesti', flag: '🇪🇪', region: 'Europe' },
  { code: 'lv', label: 'Latviešu', flag: '🇱🇻', region: 'Europe' },
  { code: 'lt', label: 'Lietuvių', flag: '🇱🇹', region: 'Europe' },
  // Africa
  { code: 'sw', label: 'Kiswahili', flag: '🇰🇪', region: 'Africa' },
  { code: 'af', label: 'Afrikaans', flag: '🇿🇦', region: 'Africa' },
];

const DEFAULT_SETTINGS: AudioInputSettings = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  sampleRate: 48000,
  channelCount: 1,
};

// Local storage keys
const SESSION_PHRASES_KEY = 'audio-input-session-phrases';
const AUDIO_SETTINGS_KEY = 'admin-audio-settings';

// Load audio settings from localStorage (shared with Settings page)
const loadAudioSettings = (): AudioInputSettings => {
  const saved = localStorage.getItem(AUDIO_SETTINGS_KEY);
  if (saved) {
    try {
      const parsed = JSON.parse(saved);
      return {
        echoCancellation: parsed.echoCancellation ?? true,
        noiseSuppression: parsed.noiseSuppression ?? true,
        autoGainControl: parsed.autoGainControl ?? true,
        sampleRate: 48000,
        channelCount: 1,
      };
    } catch {
      return DEFAULT_SETTINGS;
    }
  }
  return DEFAULT_SETTINGS;
};

export default function AudioInputPage() {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [isLoadingRooms, setIsLoadingRooms] = useState(true);
  const [selectedRoom, setSelectedRoom] = useState('1');
  const [sourceLanguage, setSourceLanguage] = useState('ko');
  const [showAllLanguages, setShowAllLanguages] = useState(false);
  const [settings, setSettings] = useState<AudioInputSettings>(loadAudioSettings);
  const [showSettings, setShowSettings] = useState(false);
  const [activeTab, setActiveTab] = useState<'audio' | 'metrics'>('audio');

  // Custom phrases state
  const [roomPhrases, setRoomPhrases] = useState<string[]>([]);
  const [sessionPhrases, setSessionPhrases] = useState<string[]>([]);
  const [newPhrase, setNewPhrase] = useState('');
  const [phraseType, setPhraseType] = useState<'room' | 'session'>('session');
  const [isLoadingPhrases, setIsLoadingPhrases] = useState(false);

  // Load rooms from API
  useEffect(() => {
    const loadRooms = async () => {
      try {
        const roomsData = await adminClient.getRooms();
        setRooms(roomsData);
        if (roomsData.length > 0 && !roomsData.find(r => r.id === selectedRoom)) {
          setSelectedRoom(roomsData[0].id);
        }
      } catch (err) {
        console.error('Failed to load rooms:', err);
      } finally {
        setIsLoadingRooms(false);
      }
    };
    loadRooms();
  }, []);

  // Sync audio settings to localStorage when changed here
  useEffect(() => {
    const audioSettingsForStorage = {
      echoCancellation: settings.echoCancellation,
      noiseSuppression: settings.noiseSuppression,
      autoGainControl: settings.autoGainControl,
    };
    localStorage.setItem(AUDIO_SETTINGS_KEY, JSON.stringify(audioSettingsForStorage));
  }, [settings.echoCancellation, settings.noiseSuppression, settings.autoGainControl]);

  const [phraseSaveStatus, setPhraseSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // Combine room and session phrases for audio capture
  const allPhrases = [...new Set([...roomPhrases, ...sessionPhrases])];

  const {
    isCapturing,
    status,
    error,
    audioLevel,
    devices,
    metrics,
    startCapture,
    stopCapture,
    loadAudioDevices,
  } = useAudioCapture(selectedRoom, sourceLanguage, allPhrases);

  // Fetch performance metrics
  const {
    metrics: performanceMetrics,
    isLoading: metricsLoading,
    error: metricsError,
  } = useMetrics(selectedRoom, {
    refreshInterval: 5000, // Refresh every 5 seconds
    enabled: activeTab === 'metrics', // Only fetch when metrics tab is active
  });

  useEffect(() => {
    loadAudioDevices();
  }, [loadAudioDevices]);

  // Load room phrases when room changes
  const loadRoomPhrases = useCallback(async () => {
    setIsLoadingPhrases(true);
    try {
      const phrases = await adminClient.getRoomPhrases(selectedRoom);
      setRoomPhrases(phrases);
    } catch (err) {
      console.error('Failed to load room phrases:', err);
    } finally {
      setIsLoadingPhrases(false);
    }
  }, [selectedRoom]);

  // Load session phrases from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(SESSION_PHRASES_KEY);
    if (stored) {
      try {
        setSessionPhrases(JSON.parse(stored));
      } catch {
        setSessionPhrases([]);
      }
    }
  }, []);

  // Load room phrases when room changes
  useEffect(() => {
    loadRoomPhrases();
  }, [loadRoomPhrases]);

  // Save session phrases to localStorage
  const saveSessionPhrases = useCallback((phrases: string[]) => {
    setSessionPhrases(phrases);
    localStorage.setItem(SESSION_PHRASES_KEY, JSON.stringify(phrases));
  }, []);

  // Add phrase handler
  const handleAddPhrase = async () => {
    const trimmed = newPhrase.trim();
    if (!trimmed) return;

    if (phraseType === 'session') {
      if (!sessionPhrases.includes(trimmed)) {
        saveSessionPhrases([...sessionPhrases, trimmed]);
      }
    } else {
      if (!roomPhrases.includes(trimmed)) {
        setPhraseSaveStatus('saving');
        try {
          const updated = await adminClient.updateRoomPhrases(selectedRoom, [...roomPhrases, trimmed]);
          setRoomPhrases(updated);
          setPhraseSaveStatus('saved');
          setTimeout(() => setPhraseSaveStatus('idle'), 2000);
        } catch (err) {
          console.error('Failed to save room phrase:', err);
          setPhraseSaveStatus('error');
        }
      }
    }
    setNewPhrase('');
  };

  // Remove phrase handler
  const handleRemovePhrase = async (phrase: string, type: 'room' | 'session') => {
    if (type === 'session') {
      saveSessionPhrases(sessionPhrases.filter(p => p !== phrase));
    } else {
      setPhraseSaveStatus('saving');
      try {
        const updated = await adminClient.updateRoomPhrases(
          selectedRoom,
          roomPhrases.filter(p => p !== phrase)
        );
        setRoomPhrases(updated);
        setPhraseSaveStatus('saved');
        setTimeout(() => setPhraseSaveStatus('idle'), 2000);
      } catch (err) {
        console.error('Failed to remove room phrase:', err);
        setPhraseSaveStatus('error');
      }
    }
  };

  const handleStart = async () => {
    try {
      console.log('[AudioInputPage] Starting audio capture...');

      // Check microphone permission status before starting
      if (navigator.permissions && navigator.permissions.query) {
        try {
          const permissionStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });
          console.log('[AudioInputPage] Microphone permission status:', permissionStatus.state);
        } catch (permErr) {
          console.warn('[AudioInputPage] Could not query microphone permission:', permErr);
        }
      }

      await startCapture(settings);
      console.log('[AudioInputPage] Audio capture started successfully');
    } catch (err: any) {
      console.error('[AudioInputPage] Failed to start capture:', err);
      console.error('[AudioInputPage] Error details:', {
        name: err?.name,
        message: err?.message,
        stack: err?.stack
      });
      // Error is already set in state by useAudioCapture hook
    }
  };

  const handleStop = () => {
    stopCapture();
  };

  const getConnectionStatus = () => {
    if (status === 'streaming') return { text: 'Streaming', color: '#48bb78' };
    if (status === 'initializing') return { text: 'Initializing...', color: '#ed8936' };
    if (status === 'reconnecting') return { text: 'Reconnecting...', color: '#f6ad55' };
    if (status === 'paused') return { text: 'Paused', color: '#a0aec0' };
    if (status === 'error') return { text: 'Error', color: '#f56565' };
    return { text: 'Disconnected', color: '#a0aec0' };
  };

  const connectionStatus = getConnectionStatus();

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Audio Input</h1>
          <p style={styles.subtitle}>Stream live audio to conference room</p>
        </div>
        <div style={{ ...styles.statusBadge, backgroundColor: connectionStatus.color }}>
          {connectionStatus.text}
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={styles.tabContainer}>
        <button
          onClick={() => setActiveTab('audio')}
          style={{
            ...styles.tab,
            ...(activeTab === 'audio' ? styles.tabActive : {}),
          }}
        >
          Audio Control
        </button>
        <button
          onClick={() => setActiveTab('metrics')}
          style={{
            ...styles.tab,
            ...(activeTab === 'metrics' ? styles.tabActive : {}),
          }}
        >
          Performance Metrics
        </button>
      </div>

      {/* Audio Control Tab */}
      {activeTab === 'audio' && (
        <>
          {error && (
            <div style={styles.error}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <span style={{ fontSize: '24px' }}>⚠️</span>
                <strong style={{ fontSize: '18px' }}>Audio Capture Error</strong>
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Type:</strong> {error.type}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Message:</strong> {error.message}
              </div>
              {error.statusCode && (
                <div style={{ marginBottom: '8px' }}>
                  <strong>Status Code:</strong> {error.statusCode}
                </div>
              )}
              <div style={{ marginTop: '12px', padding: '8px', backgroundColor: '#fff5f5', borderRadius: '4px', fontSize: '14px' }}>
                {error.type === 'device' && '🎤 Please allow microphone access in your browser settings or check your microphone connection.'}
                {error.type === 'auth' && '🔐 Please log in again to continue streaming.'}
                {error.type === 'network' && '🌐 Check your internet connection and try again.'}
                {error.type === 'validation' && '⚠️ Invalid request. Please check your settings and try again.'}
                {error.type === 'server' && '🖥️ Server error. Please try again or contact support.'}
                {error.type === 'unknown' && '❓ An unknown error occurred. Try refreshing the page.'}
              </div>
              {metrics && metrics.chunksFailed > 0 && (
                <div style={{ marginTop: '8px', fontSize: '14px' }}>
                  Failed chunks: {metrics.chunksFailed} / {metrics.chunksUploaded + metrics.chunksFailed}
                </div>
              )}
            </div>
          )}

          <div style={styles.grid}>
        {/* Room Selection */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Room Selection</h2>
          {isLoadingRooms ? (
            <p style={styles.hint}>Loading rooms...</p>
          ) : rooms.length === 0 ? (
            <p style={styles.hint}>No rooms available. Please create a room first.</p>
          ) : (
            <>
              <div style={styles.roomGrid}>
                {rooms.filter(r => r.isActive).map((room) => (
                  <button
                    key={room.id}
                    onClick={() => !isCapturing && setSelectedRoom(room.id)}
                    disabled={isCapturing}
                    style={{
                      ...styles.roomButton,
                      ...(selectedRoom === room.id ? styles.roomButtonActive : {}),
                      ...(isCapturing ? styles.roomButtonDisabled : {}),
                    }}
                  >
                    {room.name}
                  </button>
                ))}
              </div>
              <p style={styles.hint}>
                {isCapturing
                  ? `Streaming to ${rooms.find(r => r.id === selectedRoom)?.name || `Room ${selectedRoom}`}`
                  : 'Select a room to begin streaming'}
              </p>
            </>
          )}
        </div>

        {/* Source Language Selection */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Source Language (강의 언어)</h2>
          <p style={styles.hint}>Select the language you will be speaking</p>

          {/* Primary languages as buttons */}
          <div style={styles.languageGrid}>
            {PRIMARY_SOURCE_LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => !isCapturing && setSourceLanguage(lang.code)}
                disabled={isCapturing}
                style={{
                  ...styles.languageButton,
                  ...(sourceLanguage === lang.code ? styles.languageButtonActive : {}),
                  ...(isCapturing ? styles.roomButtonDisabled : {}),
                }}
              >
                <span style={styles.languageFlag}>{lang.flag}</span>
                <span style={styles.languageLabel}>{lang.label}</span>
              </button>
            ))}
          </div>

          {/* More languages toggle */}
          <div style={{ marginTop: '12px' }}>
            <button
              onClick={() => setShowAllLanguages(!showAllLanguages)}
              disabled={isCapturing}
              style={styles.moreLanguagesButton}
            >
              {showAllLanguages ? '▲ Hide more languages' : '▼ Show 50+ more languages'}
            </button>
          </div>

          {/* Extended language dropdown */}
          {showAllLanguages && (
            <div style={styles.extendedLanguageSection}>
              <select
                value={sourceLanguage}
                onChange={(e) => !isCapturing && setSourceLanguage(e.target.value)}
                disabled={isCapturing}
                style={styles.languageSelect}
              >
                {/* Group by region */}
                <optgroup label="Asia">
                  {ALL_SOURCE_LANGUAGES.filter(l => l.region === 'Asia').map(lang => (
                    <option key={lang.code} value={lang.code}>
                      {lang.flag} {lang.label} ({lang.code})
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Middle East">
                  {ALL_SOURCE_LANGUAGES.filter(l => l.region === 'Middle East').map(lang => (
                    <option key={lang.code} value={lang.code}>
                      {lang.flag} {lang.label} ({lang.code})
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Europe">
                  {ALL_SOURCE_LANGUAGES.filter(l => l.region === 'Europe').map(lang => (
                    <option key={lang.code} value={lang.code}>
                      {lang.flag} {lang.label} ({lang.code})
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Americas">
                  {ALL_SOURCE_LANGUAGES.filter(l => l.region === 'Americas').map(lang => (
                    <option key={lang.code} value={lang.code}>
                      {lang.flag} {lang.label} ({lang.code})
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Africa">
                  {ALL_SOURCE_LANGUAGES.filter(l => l.region === 'Africa').map(lang => (
                    <option key={lang.code} value={lang.code}>
                      {lang.flag} {lang.label} ({lang.code})
                    </option>
                  ))}
                </optgroup>
              </select>
            </div>
          )}

          <p style={styles.hint}>
            {isCapturing
              ? `Speaking in: ${ALL_SOURCE_LANGUAGES.find(l => l.code === sourceLanguage)?.label || sourceLanguage}`
              : 'Audio will be recognized in the selected language'}
          </p>
        </div>

        {/* Custom Phrases for Speech Recognition */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>
            Custom Phrases (음성 인식 정확도 향상)
            {phraseSaveStatus === 'saving' && <span style={styles.saveStatus}> Saving...</span>}
            {phraseSaveStatus === 'saved' && <span style={{ ...styles.saveStatus, color: '#48bb78' }}> Saved!</span>}
            {phraseSaveStatus === 'error' && <span style={{ ...styles.saveStatus, color: '#f56565' }}> Error</span>}
          </h2>
          <p style={styles.hint}>
            Add custom terms to improve speech recognition accuracy (e.g., medical terms, names)
          </p>

          {/* Add phrase input */}
          <div style={styles.phraseInputContainer}>
            <select
              value={phraseType}
              onChange={(e) => setPhraseType(e.target.value as 'room' | 'session')}
              style={styles.phraseTypeSelect}
              disabled={isCapturing}
            >
              <option value="session">Session (Temporary)</option>
              <option value="room">Room (Permanent)</option>
            </select>
            <input
              type="text"
              value={newPhrase}
              onChange={(e) => setNewPhrase(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddPhrase()}
              placeholder="Enter a phrase..."
              style={styles.phraseInput}
              disabled={isCapturing}
            />
            <button
              onClick={handleAddPhrase}
              disabled={!newPhrase.trim() || isCapturing}
              style={styles.addPhraseButton}
            >
              Add
            </button>
          </div>

          {/* Room phrases (permanent) */}
          <div style={styles.phraseSection}>
            <div style={styles.phraseSectionHeader}>
              <span style={styles.phraseSectionTitle}>Room Phrases (Permanent)</span>
              {isLoadingPhrases && <span style={styles.loadingText}>Loading...</span>}
            </div>
            <div style={styles.tagContainer}>
              {roomPhrases.length === 0 ? (
                <span style={styles.emptyText}>No room phrases</span>
              ) : (
                roomPhrases.map((phrase) => (
                  <span key={phrase} style={styles.tag}>
                    {phrase}
                    {!isCapturing && (
                      <button
                        onClick={() => handleRemovePhrase(phrase, 'room')}
                        style={styles.tagRemove}
                      >
                        x
                      </button>
                    )}
                  </span>
                ))
              )}
            </div>
          </div>

          {/* Session phrases (temporary) */}
          <div style={styles.phraseSection}>
            <div style={styles.phraseSectionHeader}>
              <span style={styles.phraseSectionTitle}>Session Phrases (This session only)</span>
            </div>
            <div style={styles.tagContainer}>
              {sessionPhrases.length === 0 ? (
                <span style={styles.emptyText}>No session phrases</span>
              ) : (
                sessionPhrases.map((phrase) => (
                  <span key={phrase} style={{ ...styles.tag, backgroundColor: '#e2e8f0' }}>
                    {phrase}
                    {!isCapturing && (
                      <button
                        onClick={() => handleRemovePhrase(phrase, 'session')}
                        style={styles.tagRemove}
                      >
                        x
                      </button>
                    )}
                  </span>
                ))
              )}
            </div>
          </div>

          <p style={styles.hint}>
            Total: {allPhrases.length} custom phrases will be used for recognition
          </p>
        </div>

        {/* Audio Controls */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Audio Controls</h2>

          {devices.length > 0 && (
            <div style={styles.inputGroup}>
              <label htmlFor="device" style={styles.label}>
                Microphone
              </label>
              <select
                id="device"
                value={settings.deviceId || ''}
                onChange={(e) =>
                  setSettings((prev) => ({ ...prev, deviceId: e.target.value }))
                }
                disabled={isCapturing}
                style={styles.select}
              >
                <option value="">Default Microphone</option>
                {devices.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || `Microphone ${device.deviceId.slice(0, 8)}`}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div style={styles.buttonGroup}>
            {!isCapturing ? (
              <button onClick={handleStart} style={styles.startButton}>
                Start Streaming
              </button>
            ) : (
              <button onClick={handleStop} style={styles.stopButton}>
                Stop Streaming
              </button>
            )}

            <button
              onClick={() => setShowSettings(!showSettings)}
              disabled={isCapturing}
              style={styles.settingsButton}
            >
              {showSettings ? 'Hide Settings' : 'Show Settings'}
            </button>
          </div>

          {showSettings && (
            <div style={styles.settingsPanel}>
              <h3 style={styles.settingsTitle}>Advanced Settings</h3>

              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={settings.echoCancellation}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      echoCancellation: e.target.checked,
                    }))
                  }
                  disabled={isCapturing}
                />
                Echo Cancellation
              </label>

              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={settings.noiseSuppression}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      noiseSuppression: e.target.checked,
                    }))
                  }
                  disabled={isCapturing}
                />
                Noise Suppression
              </label>

              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={settings.autoGainControl}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      autoGainControl: e.target.checked,
                    }))
                  }
                  disabled={isCapturing}
                />
                Auto Gain Control
              </label>

              <div style={styles.inputGroup}>
                <label htmlFor="sampleRate" style={styles.label}>
                  Sample Rate
                </label>
                <select
                  id="sampleRate"
                  value={settings.sampleRate}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      sampleRate: parseInt(e.target.value),
                    }))
                  }
                  disabled={isCapturing}
                  style={styles.select}
                >
                  <option value="16000">16 kHz</option>
                  <option value="44100">44.1 kHz</option>
                  <option value="48000">48 kHz</option>
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Audio Visualizer */}
      <div style={styles.card}>
        <h2 style={styles.cardTitle}>Audio Visualization</h2>
        <AudioVisualizer audioLevel={audioLevel} isActive={isCapturing} />

        {isCapturing && (
          <div style={styles.qualityIndicator}>
            <span style={styles.label}>Signal Quality:</span>
            <span
              style={{
                ...styles.qualityBadge,
                backgroundColor: getQualityColor(audioLevel),
              }}
            >
              {getQualityLabel(audioLevel)}
            </span>
          </div>
        )}
      </div>

      {/* Upload Metrics */}
      {isCapturing && metrics && (
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Upload Performance</h2>
          <div style={styles.metricsGrid}>
            <div style={styles.metricItem}>
              <div style={styles.metricLabel}>Chunks Uploaded</div>
              <div style={styles.metricValue}>{metrics.chunksUploaded}</div>
            </div>
            <div style={styles.metricItem}>
              <div style={styles.metricLabel}>Failed Chunks</div>
              <div style={{ ...styles.metricValue, color: metrics.chunksFailed > 0 ? '#f56565' : '#48bb78' }}>
                {metrics.chunksFailed}
              </div>
            </div>
            <div style={styles.metricItem}>
              <div style={styles.metricLabel}>Current Latency</div>
              <div style={styles.metricValue}>{metrics.currentLatency}ms</div>
            </div>
            <div style={styles.metricItem}>
              <div style={styles.metricLabel}>Average Latency</div>
              <div style={styles.metricValue}>{metrics.averageLatency}ms</div>
            </div>
          </div>
        </div>
      )}
        </>
      )}

      {/* Performance Metrics Tab */}
      {activeTab === 'metrics' && (
        <PerformanceDashboard
          metrics={performanceMetrics}
          isLoading={metricsLoading}
          error={metricsError}
        />
      )}
    </div>
  );
}

function getQualityLabel(level: number): string {
  if (level < 10) return 'Too Quiet';
  if (level < 30) return 'Good';
  if (level < 70) return 'Excellent';
  return 'Too Loud';
}

function getQualityColor(level: number): string {
  if (level < 10 || level > 70) return '#f56565';
  if (level < 30) return '#48bb78';
  return '#38b2ac';
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '24px',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#1a202c',
    margin: '0 0 4px 0',
  },
  subtitle: {
    fontSize: '16px',
    color: '#718096',
    margin: 0,
  },
  statusBadge: {
    padding: '8px 16px',
    borderRadius: '20px',
    color: 'white',
    fontWeight: '600',
    fontSize: '14px',
  },
  error: {
    padding: '16px',
    backgroundColor: '#fee',
    border: '1px solid #fcc',
    borderRadius: '8px',
    color: '#c00',
    marginBottom: '24px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
    gap: '24px',
    marginBottom: '24px',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  cardTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#2d3748',
    marginTop: 0,
    marginBottom: '16px',
  },
  roomGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
    marginBottom: '12px',
  },
  roomButton: {
    padding: '16px',
    border: '2px solid #cbd5e0',
    borderRadius: '8px',
    backgroundColor: 'white',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  roomButtonActive: {
    backgroundColor: '#667eea',
    borderColor: '#667eea',
    color: 'white',
  },
  roomButtonDisabled: {
    cursor: 'not-allowed',
    opacity: 0.5,
  },
  languageGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
    marginBottom: '12px',
  },
  languageButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    border: '2px solid #cbd5e0',
    borderRadius: '8px',
    backgroundColor: 'white',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  languageButtonActive: {
    backgroundColor: '#667eea',
    borderColor: '#667eea',
    color: 'white',
  },
  languageFlag: {
    fontSize: '24px',
  },
  languageLabel: {
    flex: 1,
    textAlign: 'left' as const,
  },
  hint: {
    fontSize: '14px',
    color: '#718096',
    margin: '8px 0 0 0',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginBottom: '16px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#2d3748',
  },
  select: {
    padding: '10px 12px',
    border: '1px solid #cbd5e0',
    borderRadius: '6px',
    fontSize: '14px',
    backgroundColor: 'white',
  },
  buttonGroup: {
    display: 'flex',
    gap: '12px',
  },
  startButton: {
    flex: 1,
    padding: '12px',
    backgroundColor: '#48bb78',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  stopButton: {
    flex: 1,
    padding: '12px',
    backgroundColor: '#f56565',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  settingsButton: {
    padding: '12px 24px',
    backgroundColor: '#e2e8f0',
    color: '#2d3748',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  settingsPanel: {
    marginTop: '16px',
    padding: '16px',
    backgroundColor: '#f7fafc',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
  },
  settingsTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#2d3748',
    marginTop: 0,
    marginBottom: '12px',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: '#2d3748',
    marginBottom: '8px',
    cursor: 'pointer',
  },
  qualityIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid #e2e8f0',
  },
  qualityBadge: {
    padding: '4px 12px',
    borderRadius: '12px',
    color: 'white',
    fontSize: '12px',
    fontWeight: '600',
  },
  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '16px',
  },
  metricItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  metricLabel: {
    fontSize: '14px',
    color: '#718096',
    fontWeight: '500',
  },
  metricValue: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#2d3748',
  },
  tabContainer: {
    display: 'flex',
    gap: '8px',
    marginBottom: '24px',
    borderBottom: '2px solid #e2e8f0',
  },
  tab: {
    padding: '12px 24px',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: '3px solid transparent',
    fontSize: '16px',
    fontWeight: '600',
    color: '#718096',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  tabActive: {
    color: '#667eea',
    borderBottomColor: '#667eea',
  },
  // Phrase management styles
  saveStatus: {
    fontSize: '14px',
    fontWeight: 'normal',
    marginLeft: '8px',
    color: '#718096',
  },
  phraseInputContainer: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
  },
  phraseTypeSelect: {
    padding: '8px 12px',
    border: '1px solid #cbd5e0',
    borderRadius: '6px',
    fontSize: '14px',
    backgroundColor: 'white',
    minWidth: '160px',
  },
  phraseInput: {
    flex: 1,
    padding: '8px 12px',
    border: '1px solid #cbd5e0',
    borderRadius: '6px',
    fontSize: '14px',
  },
  addPhraseButton: {
    padding: '8px 16px',
    backgroundColor: '#667eea',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  phraseSection: {
    marginBottom: '16px',
  },
  phraseSectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  phraseSectionTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#4a5568',
  },
  loadingText: {
    fontSize: '12px',
    color: '#718096',
    fontStyle: 'italic',
  },
  tagContainer: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '8px',
    minHeight: '32px',
    padding: '8px',
    backgroundColor: '#f7fafc',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
  },
  tag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 10px',
    backgroundColor: '#667eea',
    color: 'white',
    borderRadius: '16px',
    fontSize: '13px',
    fontWeight: '500',
  },
  tagRemove: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '16px',
    height: '16px',
    padding: 0,
    border: 'none',
    borderRadius: '50%',
    backgroundColor: 'rgba(255,255,255,0.3)',
    color: 'inherit',
    fontSize: '12px',
    cursor: 'pointer',
    marginLeft: '2px',
  },
  emptyText: {
    fontSize: '13px',
    color: '#a0aec0',
    fontStyle: 'italic',
  },
  // Extended language selection styles
  moreLanguagesButton: {
    padding: '8px 16px',
    backgroundColor: '#f7fafc',
    color: '#4a5568',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    width: '100%',
    transition: 'all 0.2s',
  },
  extendedLanguageSection: {
    marginTop: '12px',
    padding: '12px',
    backgroundColor: '#f7fafc',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
  },
  languageSelect: {
    width: '100%',
    padding: '12px',
    border: '1px solid #cbd5e0',
    borderRadius: '6px',
    fontSize: '14px',
    backgroundColor: 'white',
  },
};
